import { useEffect, useRef, useState } from 'react'
import { apiClient, Product, ProductState, ImageSearchResult, Statistics, Checkpoint, getApiError } from '../api'
import { ImageSelector } from './ImageSelector'
import { NoImageOptions } from './NoImageOptions'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { useTheme } from '../contexts/ThemeContext'
import { Moon, Sun, Settings, ChevronLeft, ChevronRight, Search, RefreshCw, Upload, Check, Download, AlertCircle, CheckCircle, Hash, Loader, Folder, Images, Menu, X } from 'lucide-react'
import { Toast } from './Toast'

interface ConferenceScreenProps {
  onSessionCompleted: () => void
  onError: (error: string) => void
  onGoToDashboard?: () => void
  onGoToGallery?: () => void
  onGoToBrands?: () => void
  onGoToExternalUpload?: () => void
  onGoToManualSearch?: () => void
  products?: Product[]
  checkpoint?: Checkpoint | null
  currentProductData?: { product: Product; state: ProductState; stats: Statistics; checkpoint: { index: number; total: number } } | null
  onCurrentProductLoaded?: (data: { product: Product; state: ProductState; stats: Statistics; checkpoint: { index: number; total: number } }) => void
}

export function ConferenceScreen({ onSessionCompleted, onError, onGoToDashboard, onGoToGallery, onGoToBrands, onGoToExternalUpload, onGoToManualSearch, products = [], checkpoint: initialCheckpoint, currentProductData, onCurrentProductLoaded }: ConferenceScreenProps) {
  const { theme, toggleTheme, logo } = useTheme()
  const [product, setProduct] = useState<Product | null>(null)
  const [state, setProductState] = useState<ProductState | null>(null)
  const [stats, setStats] = useState<Statistics | null>(null)
  const [checkpointIndex, setCheckpointIndex] = useState(0)
  const [totalProducts, setTotalProducts] = useState(0)

  const [searchQuery, setSearchQuery] = useState('')
  const [images, setImages] = useState<ImageSearchResult[]>([])
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [searchOffset, setSearchOffset] = useState(0)

  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [dragOverUpload, setDragOverUpload] = useState(false)
  const [codeSearch, setCodeSearch] = useState('')
  const [returnCode, setReturnCode] = useState<string | null>(null)
  const [temporaryMode, setTemporaryMode] = useState<'search' | 'pending' | null>(null)
  const [isJumping, setIsJumping] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navSequenceRef = useRef(0)
  const serverNavQueueRef = useRef<Promise<unknown>>(Promise.resolve())

  const showLocalProductAt = (index: number, optimisticStats?: Statistics) => {
    if (!products.length || index < 0 || index >= products.length) return null
    const p = products[index]
    const localState: ProductState = { status: 'PENDING', updatedAt: new Date().toISOString() }
    setProduct(p)
    setProductState(localState)
    if (optimisticStats) setStats(optimisticStats)
    setCheckpointIndex(index)
    setTotalProducts(products.length)
    setSelectedImageIndex(null)
    restoreSearchCache(p.codigo, p.descricao || p.codigo)
    const data = { product: p, state: localState, stats: optimisticStats || stats!, checkpoint: { index, total: products.length } }
    if (optimisticStats && onCurrentProductLoaded) onCurrentProductLoaded(data)
    return p
  }

  const queueServerNavigation = (direction: 'next' | 'previous', sequence: number) => {
    serverNavQueueRef.current = serverNavQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const response = direction === 'next' ? await apiClient.getNextProduct() : await apiClient.getPreviousProduct()
        if (navSequenceRef.current === sequence) applyProductData(response.data)
        return response
      })
      .catch(err => {
        if (navSequenceRef.current === sequence) setError(getApiError(err, direction === 'next' ? 'Erro ao avançar' : 'Erro ao voltar'))
      })
  }

  const refreshStatsInBackground = () => {
    void apiClient.getStats().then(r => setStats(r.data)).catch(() => undefined)
  }

  const restoreSearchCache = (code: string, fallbackQuery: string) => {
    try {
      const raw = localStorage.getItem(`imageSearchCache:${code}`)
      if (!raw) { setImages([]); setSearchQuery(fallbackQuery); setSearchOffset(0); return }
      const cached = JSON.parse(raw)
      setImages(Array.isArray(cached.images) ? cached.images : [])
      setSearchQuery(cached.query || fallbackQuery)
      setSearchOffset(Number(cached.offset || 0))
    } catch { setImages([]); setSearchQuery(fallbackQuery); setSearchOffset(0) }
  }

  useEffect(() => {
    if (!error) return
    const timer = setTimeout(() => setError(null), 2000)
    return () => clearTimeout(timer)
  }, [error])

  // Carregar produto atual ao montar - usar dados em cache se disponível
  useEffect(() => {
    if (currentProductData) {
      const { product: p, state: s, stats: st, checkpoint } = currentProductData
      setProduct(p)
      setProductState(s)
      setStats(st)
      setCheckpointIndex(checkpoint.index)
      setTotalProducts(checkpoint.total)

      const defaultQuery = p.descricao || p.codigo
      setSelectedImageIndex(null)
      restoreSearchCache(p.codigo, defaultQuery)
    } else {
      // Se não tem cache, carrega normalmente
      loadCurrentProduct()
    }
  }, [currentProductData])

  // Carregar preview autenticado de imagem aprovada/arquivada
  useEffect(() => {
    let objectUrl: string | null = null
    const loadPreview = async () => {
      if (!product || (state?.status !== 'APPROVED' && state?.status !== 'ARCHIVED')) {
        setPreviewImage(null)
        return
      }
      try {
        const response = await apiClient.getProductImageBlob(product.codigo)
        objectUrl = URL.createObjectURL(response.data)
        setPreviewImage(objectUrl)
      } catch (err) {
        setPreviewImage(null)
        setError(getApiError(err, 'Erro ao carregar pré-visualização'))
      }
    }
    loadPreview()
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [product?.codigo, state?.status])

  // Auto-pesquisar quando produto muda (apenas se não foi aprovado)
  useEffect(() => {
    if (product && state?.status !== 'APPROVED' && state?.status !== 'ARCHIVED' && state?.status !== 'DELETED' && state?.status !== 'DEACTIVATED' && images.length === 0 && !isSearching) {
      const timer = setTimeout(() => {
        const defaultQuery = product.descricao || product.codigo
        handleSearch(defaultQuery, 0)
      }, 180)
      return () => clearTimeout(timer)
    }
  }, [product?.codigo])

  const loadCurrentProduct = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getCurrentProduct()

      const { product: p, state: s, stats: st, checkpoint } = response.data

      const data = { product: p, state: s, stats: st, checkpoint }

      setProduct(p)
      setProductState(s)
      setStats(st)
      setCheckpointIndex(checkpoint.index)
      setTotalProducts(checkpoint.total)

      const defaultQuery = p.descricao || p.codigo
      setSelectedImageIndex(null)
      restoreSearchCache(p.codigo, defaultQuery)

      // Salvar no App para persistir quando voltar
      if (onCurrentProductLoaded) {
        onCurrentProductLoaded(data)
      }
    } catch (err) {
      const message = getApiError(err, 'Erro ao carregar produto')
      setError(message)
      onError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const applyProductData = (data: { product: Product; state: ProductState; stats: Statistics; checkpoint: { index: number; total: number } }) => {
    const { product: p, state: s, stats: st, checkpoint } = data
    setProduct(p)
    setProductState(s)
    setStats(st)
    setCheckpointIndex(checkpoint.index)
    setTotalProducts(checkpoint.total)
    setSelectedImageIndex(null)
    restoreSearchCache(p.codigo, p.descricao || p.codigo)
    if (onCurrentProductLoaded) onCurrentProductLoaded(data)
  }

  const jumpToCode = async (codigo: string, mode: 'search' | 'pending') => {
    if (!product) return
    const clean = codigo.trim()
    if (!clean) { setError('Digite um código para pesquisar'); return }
    try {
      setIsJumping(true)
      setError(null)
      if (!returnCode) setReturnCode(product.codigo)
      const response = await apiClient.jumpToProduct(clean)
      setTemporaryMode(mode)
      applyProductData(response.data)
      setCodeSearch('')
    } catch (err) {
      setError(getApiError(err, 'Código não encontrado'))
    } finally { setIsJumping(false) }
  }

  const handleReturnToOrigin = async () => {
    if (!returnCode) return
    const target = returnCode
    try {
      setIsJumping(true)
      const response = await apiClient.jumpToProduct(target)
      setReturnCode(null)
      setTemporaryMode(null)
      applyProductData(response.data)
    } catch (err) {
      setError(getApiError(err, 'Erro ao voltar para o produto anterior'))
    } finally { setIsJumping(false) }
  }

  const handleFindUnclassified = async () => {
    if (!product) return
    try {
      setIsJumping(true)
      setError(null)
      const response = await apiClient.getUnclassifiedProduct(product.codigo)
      await jumpToCode(response.data.product.codigo, 'pending')
    } catch (err) {
      setError(getApiError(err, 'Nenhum item pendente ficou para trás'))
    } finally { setIsJumping(false) }
  }

  const handleSearch = async (query?: string, newOffset?: number) => {
    if (!product) return

    try {
      setIsSearching(true)
      setError(null)
      const finalQuery = query || searchQuery
      const offset = newOffset !== undefined ? newOffset : 0

      const response = await apiClient.search(product.codigo, finalQuery, undefined, offset)

      if (response.data.error) {
        setError(response.data.error)
        setImages([])
        return
      }

      // Limpar seleção anterior e mostrar apenas os 5 novos
      setImages(response.data.images)
      setSelectedImageIndex(null)
      setSearchOffset(offset)
      localStorage.setItem(`imageSearchCache:${product.codigo}`, JSON.stringify({ images: response.data.images, query: finalQuery, offset, savedAt: Date.now() }))

      if (response.data.images.length === 0) {
        setError('Nenhuma imagem encontrada')
      }
    } catch (err) {
      const message = getApiError(err, 'Erro na pesquisa')
      setError(message)
      setImages([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleNextPage = async () => {
    const nextOffset = searchOffset + 5
    await handleSearch(searchQuery, nextOffset)
  }

  const moveForwardImmediately = (optimisticStats?: Statistics) => {
    const targetIndex = Math.min(checkpointIndex + 1, Math.max(0, products.length - 1))
    const sequence = ++navSequenceRef.current
    if (products.length && targetIndex !== checkpointIndex) showLocalProductAt(targetIndex, optimisticStats || stats || undefined)
    queueServerNavigation('next', sequence)
  }

  const handleApprove = () => {
    if (!product || selectedImageIndex === null) return
    const currentCode = product.codigo
    const selectedImage = images[selectedImageIndex]
    const optimisticStats = stats ? { ...stats, approved: stats.approved + (state?.status === 'APPROVED' ? 0 : 1), pending: Math.max(0, stats.pending - (state?.status === 'PENDING' || !state ? 1 : 0)) } : undefined

    if (temporaryMode === 'pending' && returnCode) {
      setIsLoading(true)
      void apiClient.approveImage(currentCode, selectedImage, searchQuery)
        .then(() => handleReturnToOrigin())
        .catch(err => setError(getApiError(err, 'Erro ao aprovar')))
        .finally(() => setIsLoading(false))
      return
    }

    const action = apiClient.approveImage(currentCode, selectedImage, searchQuery)
    moveForwardImmediately(optimisticStats)
    void action.then(refreshStatsInBackground).catch(err => setError(getApiError(err, 'Erro ao aprovar')))
  }

  const handleSkip = () => {
    if (!product) return
    const currentCode = product.codigo
    const optimisticStats = stats ? { ...stats, skipped: stats.skipped + (state?.status === 'SKIPPED' ? 0 : 1), pending: Math.max(0, stats.pending - (state?.status === 'PENDING' || !state ? 1 : 0)) } : undefined
    if (temporaryMode === 'pending' && returnCode) {
      setIsLoading(true)
      void apiClient.skipProduct(currentCode).then(() => handleReturnToOrigin()).catch(err => setError(getApiError(err, 'Erro ao pular'))).finally(() => setIsLoading(false))
      return
    }
    const action = apiClient.skipProduct(currentCode)
    moveForwardImmediately(optimisticStats)
    void action.then(refreshStatsInBackground).catch(err => setError(getApiError(err, 'Erro ao pular')))
  }


  const handleDeactivate = () => {
    if (!product) return
    const currentCode = product.codigo
    const optimisticStats = stats ? {
      ...stats,
      deactivated: (stats.deactivated || 0) + (state?.status === 'DEACTIVATED' ? 0 : 1),
      pending: Math.max(0, stats.pending - (state?.status === 'PENDING' || !state ? 1 : 0))
    } : undefined

    if (temporaryMode === 'pending' && returnCode) {
      setIsLoading(true)
      void apiClient.deactivateProduct(currentCode)
        .then(() => handleReturnToOrigin())
        .catch(err => setError(getApiError(err, 'Erro ao desativar produto')))
        .finally(() => setIsLoading(false))
      return
    }

    const action = apiClient.deactivateProduct(currentCode)
    moveForwardImmediately(optimisticStats)
    void action.then(refreshStatsInBackground).catch(err => setError(getApiError(err, 'Erro ao desativar produto')))
  }

  const handleNoImage = () => {
    if (!product) return
    const currentCode = product.codigo
    const optimisticStats = stats ? { ...stats, noImage: stats.noImage + (state?.status === 'NO_IMAGE' ? 0 : 1), pending: Math.max(0, stats.pending - (state?.status === 'PENDING' || !state ? 1 : 0)) } : undefined
    if (temporaryMode === 'pending' && returnCode) {
      setIsLoading(true)
      void apiClient.markNoImage(currentCode).then(() => handleReturnToOrigin()).catch(err => setError(getApiError(err, 'Erro ao marcar sem imagem'))).finally(() => setIsLoading(false))
      return
    }
    const action = apiClient.markNoImage(currentCode)
    moveForwardImmediately(optimisticStats)
    void action.then(refreshStatsInBackground).catch(err => setError(getApiError(err, 'Erro ao marcar sem imagem')))
  }

  const handlePrevious = () => {
    if (checkpointIndex <= 0) return
    const sequence = ++navSequenceRef.current
    showLocalProductAt(checkpointIndex - 1, stats || undefined)
    queueServerNavigation('previous', sequence)
  }

  const handleNext = () => {
    if (checkpointIndex >= totalProducts - 1) return
    moveForwardImmediately(stats || undefined)
  }

  const handleCompleteSession = async () => {
    try {
      setIsLoading(true)
      await apiClient.completeSession()
      onSessionCompleted()
    } catch (err) {
      const message = getApiError(err, 'Erro ao finalizar')
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearData = async () => {
    try {
      setIsLoading(true)
      await apiClient.clearAllData()
      window.location.reload()
    } catch (err) {
      const message = getApiError(err, 'Erro ao limpar dados')
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleImageUpload = (file: File) => {
    if (!product) return
    const currentCode = product.codigo
    setError(null)
    if (temporaryMode === 'pending' && returnCode) {
      setIsUploadingImage(true)
      void apiClient.approveLocalFile(currentCode, file, `Upload: ${file.name}`)
        .then(() => handleReturnToOrigin())
        .catch(err => setError(getApiError(err, 'Erro ao fazer upload')))
        .finally(() => setIsUploadingImage(false))
      return
    }
    const optimisticStats = stats ? { ...stats, approved: stats.approved + (state?.status === 'APPROVED' ? 0 : 1), pending: Math.max(0, stats.pending - (state?.status === 'PENDING' || !state ? 1 : 0)) } : undefined
    const action = apiClient.approveLocalFile(currentCode, file, `Upload: ${file.name}`)
    moveForwardImmediately(optimisticStats)
    void action.then(refreshStatsInBackground).catch(err => setError(getApiError(err, 'Erro ao fazer upload')))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverUpload(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverUpload(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverUpload(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        handleImageUpload(file)
      } else {
        setError('Por favor, selecione um arquivo de imagem')
      }
    }
  }

  const handleDownloadCurrent = async () => {
    if (!product) return
    try {
      const response = await apiClient.downloadProductImageBlob(product.codigo)
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `${product.codigo}.jpg`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(getApiError(err, 'Erro ao baixar imagem'))
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        handleImageUpload(file)
      } else {
        setError('Por favor, selecione um arquivo de imagem')
      }
    }
    // Limpar o input para poder selecionar o mesmo arquivo novamente
    e.currentTarget.value = ''
  }

  useKeyboardShortcuts({
    '1': () => setSelectedImageIndex(0),
    '2': () => setSelectedImageIndex(1),
    '3': () => setSelectedImageIndex(2),
    '4': () => setSelectedImageIndex(3),
    '5': () => setSelectedImageIndex(4),
    'Enter': () => selectedImageIndex !== null && handleApprove(),
    'n': () => setSelectedImageIndex(null),
    'r': () => handleSearch(),
    's': () => handleSkip()
  })

  if (!product || !stats) {
    return (
      <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin mb-4">
            <svg className="h-12 w-12 text-primary-600 dark:text-primary-400 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Carregando produto...</p>
        </div>
      </div>
    )
  }

  const progress = Math.round(((checkpointIndex + 1) / totalProducts) * 100)

  return (
    <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark transition-colors duration-300">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-[#18181A] backdrop-blur-md border-b border-gray-200 dark:border-[#5A5A5E]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          {/* Logo + Título */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <img
              src={logo}
              alt="Seridóplast"
              className="h-8 sm:h-10 w-auto object-contain flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] bg-clip-text text-transparent truncate">
                Gerenciador de Imagens
              </h1>
              <p className="text-xs sm:text-sm text-[#5A5A5E] dark:text-[#D9D9D9]">
                Produto {checkpointIndex + 1} de {totalProducts} • <span className="font-bold text-[#E5191F] dark:text-[#FCD016]">{progress}%</span>
              </p>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-[#232326] hover:bg-gray-200 dark:hover:bg-[#5A5A5E] transition-colors flex-shrink-0"
              title="Alternar tema"
            >
              {theme === 'light' ? (
                <Moon size={18} className="text-[#414142]" />
              ) : (
                <Sun size={18} className="text-[#FCD016]" />
              )}
            </button>

            {onGoToManualSearch && (
              <button
                onClick={onGoToManualSearch}
                className="px-3 sm:px-4 py-2 rounded-lg bg-[#414142] hover:bg-[#2F2F31] text-white font-semibold transition-all hover:shadow-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-shrink-0"
                title="Abrir Pesquisa Avulsa"
              >
                <Search size={16} className="flex-shrink-0" />
                <span className="hidden sm:inline">Pesquisa Avulsa</span>
              </button>
            )}

            {onGoToExternalUpload && (
              <button
                onClick={onGoToExternalUpload}
                className="px-3 sm:px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-semibold transition-all hover:shadow-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-shrink-0"
                title="Importar imagens externas por código"
              >
                <Images size={16} className="flex-shrink-0" />
                <span className="hidden sm:inline">Importar imagens</span>
              </button>
            )}

            {onGoToDashboard && (
              <button
                onClick={onGoToDashboard}
                className="px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] text-white dark:text-[#18181A] font-semibold transition-all hover:shadow-lg hover:scale-105 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-shrink-0"
              >
                <Settings size={16} className="flex-shrink-0" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            )}
          </div>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="sm:hidden shrink-0 w-10 h-10 rounded-lg bg-[#414142] text-white grid place-items-center"
            aria-label="Abrir menu"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-[#5A5A5E]">
          <div
            className="h-full bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[90] sm:hidden">
          <button className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} aria-label="Fechar menu" />
          <aside className="absolute right-0 top-0 h-full w-[82vw] max-w-sm bg-white dark:bg-[#232326] shadow-2xl p-4 flex flex-col">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-200 dark:border-[#414142]">
              <div><p className="font-black">Menu</p><p className="text-xs text-gray-500">Navegação do sistema</p></div>
              <button onClick={() => setMobileMenuOpen(false)} className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-[#18181A] grid place-items-center"><X size={18}/></button>
            </div>
            <div className="grid gap-2">
              <button onClick={() => { toggleTheme(); setMobileMenuOpen(false) }} className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-[#18181A] font-bold flex items-center gap-3">{theme === 'light' ? <Moon size={18}/> : <Sun size={18}/>}Alternar tema</button>
              {onGoToManualSearch && <button onClick={() => { setMobileMenuOpen(false); onGoToManualSearch() }} className="w-full px-4 py-3 rounded-xl bg-[#414142] text-white font-bold flex items-center gap-3"><Search size={18}/>Pesquisa Avulsa</button>}
              {onGoToExternalUpload && <button onClick={() => { setMobileMenuOpen(false); onGoToExternalUpload() }} className="w-full px-4 py-3 rounded-xl bg-slate-700 text-white font-bold flex items-center gap-3"><Images size={18}/>Importar imagens</button>}
              {onGoToDashboard && <button onClick={() => { setMobileMenuOpen(false); onGoToDashboard() }} className="w-full px-4 py-3 rounded-xl bg-[#E5191F] text-white font-bold flex items-center gap-3"><Settings size={18}/>Dashboard</button>}
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-full mx-auto px-3 sm:px-6 py-3 sm:py-6 min-h-[calc(100vh-80px)] sm:h-[calc(100vh-120px)] sm:overflow-hidden bg-gradient-light dark:bg-gradient-dark">
        <Toast message={error} type="error" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 h-full sm:overflow-y-auto">
          {/* Left: Product Info (1 col) */}
          <div className="block order-1 sm:order-none col-span-1 sm:col-span-2 lg:col-span-1 space-y-2 sm:space-y-3 sm:overflow-y-auto scrollbar-hide sm:max-h-full">
            {/* Product Details */}
            <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E]">
              <h2 className="text-xs font-semibold text-[#414142] dark:text-[#D9D9D9] mb-2 sm:mb-3 uppercase tracking-wide">Produto</h2>

              <div className="space-y-1 sm:space-y-2 text-sm">
                <div>
                  <label className="text-xs font-semibold text-[#5A5A5E] dark:text-[#D9D9D9]">Código</label>
                  <p className="text-base font-mono font-bold text-[#E5191F] dark:text-[#FCD016] break-all">{product.codigo}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#5A5A5E] dark:text-[#D9D9D9]">Descrição</label>
                  <p className="text-xs text-[#414142] dark:text-[#D9D9D9] line-clamp-3">{product.descricao}</p>
                </div>

                {product.marca && (
                  <div>
                    <label className="text-xs font-semibold text-[#5A5A5E] dark:text-[#D9D9D9]">Marca</label>
                    <p className="text-xs text-[#414142] dark:text-[#D9D9D9]">{product.marca}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Busca rápida / item perdido */}
            <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E]">
              <h2 className="text-xs font-semibold text-[#414142] dark:text-[#D9D9D9] mb-2 uppercase tracking-wide">Navegação rápida</h2>

              <div className="space-y-2">
                <div className="flex items-stretch gap-2">
                  <div className="relative min-w-0 flex-1">
                    <Hash size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      value={codeSearch}
                      onChange={e => setCodeSearch(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') jumpToCode(codeSearch, 'search') }}
                      placeholder="Código do item"
                      className="h-[40px] w-full pl-8 pr-2 rounded-lg border border-gray-300 dark:border-[#5A5A5E] bg-gray-50 dark:bg-[#18181A] text-xs outline-none focus:ring-2 focus:ring-[#E5191F]/30"
                    />
                  </div>

                  <button
                    onClick={() => jumpToCode(codeSearch, 'search')}
                    disabled={isJumping || !codeSearch.trim()}
                    className="h-[40px] shrink-0 px-3 rounded-lg bg-[#414142] hover:bg-[#2f2f31] text-white font-semibold text-xs flex items-center justify-center gap-1 disabled:opacity-50 transition-colors"
                    title="Buscar código"
                  >
                    <Search size={14} />
                    <span className="hidden xl:inline">Buscar</span>
                  </button>

                  <button
                    onClick={handleReturnToOrigin}
                    disabled={!returnCode || isJumping}
                    className="h-[40px] shrink-0 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs flex items-center justify-center gap-1 disabled:opacity-40 transition-colors"
                    title="Voltar para onde estava antes da pesquisa"
                  >
                    <ChevronLeft size={14} />
                    <span className="hidden xl:inline">Voltar</span>
                  </button>
                </div>

                <button
                  onClick={handleFindUnclassified}
                  disabled={isJumping}
                  className="w-full min-h-[38px] px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold text-xs flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                  title="Procurar um item pendente que ficou para trás"
                >
                  <RefreshCw size={14} className={isJumping ? 'animate-spin' : ''} />
                  Item perdido
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E]">
              <h2 className="text-xs font-semibold text-[#414142] dark:text-[#D9D9D9] mb-2 sm:mb-3 uppercase tracking-wide">Progresso</h2>

              <div className="space-y-1 sm:space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[#5A5A5E] dark:text-[#D9D9D9]">Aprovados</span>
                  <span className="font-bold text-green-600 dark:text-green-400">{stats.approved}</span>
                </div>
                {(stats.archived || 0) > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#5A5A5E] dark:text-[#D9D9D9]">Arquivadas</span>
                    <span className="font-bold text-slate-600 dark:text-slate-300">{stats.archived}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[#5A5A5E] dark:text-[#D9D9D9]">Pulados</span>
                  <span className="font-bold text-orange-600 dark:text-orange-400">{stats.skipped}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#5A5A5E] dark:text-[#D9D9D9]">Sem Imagem</span>
                  <span className="font-bold text-[#5A5A5E] dark:text-[#D9D9D9]">{stats.noImage}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#5A5A5E] dark:text-[#D9D9D9]">Pendentes</span>
                  <span className="font-bold text-[#E5191F] dark:text-[#FCD016]">{stats.pending}</span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handlePrevious}
                disabled={isLoading || checkpointIndex === 0}
                className="px-2 sm:px-3 py-2 rounded-lg bg-gray-100 dark:bg-[#232326] hover:bg-gray-200 dark:hover:bg-[#5A5A5E] text-[#414142] dark:text-[#D9D9D9] font-semibold text-xs sm:text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <ChevronLeft size={14} className="sm:block hidden" />
                <ChevronLeft size={12} className="sm:hidden" />
                <span className="hidden sm:inline">Voltar</span>
              </button>
              <button
                onClick={handleNext}
                disabled={isLoading}
                className="px-2 sm:px-3 py-2 rounded-lg bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] text-white dark:text-[#18181A] font-semibold text-xs sm:text-sm transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <span className="hidden sm:inline">Avançar</span>
                <ChevronRight size={14} className="sm:block hidden" />
                <ChevronRight size={12} className="sm:hidden" />
              </button>
            </div>
          </div>

          {/* Center: Image Grid & Preview (2 cols) */}
          <div className="order-2 sm:order-none col-span-1 sm:col-span-2 lg:col-span-2 flex flex-col gap-2 sm:gap-3">
            {/* Image Grid */}
            {images.length > 0 ? (
              <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E] flex-1 flex flex-col animate-fade-in">
                <h2 className="text-xs font-semibold text-[#414142] dark:text-[#D9D9D9] mb-2 sm:mb-3 uppercase tracking-wide">Imagens Encontradas</h2>

                {/* Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 sm:gap-2 mb-2 sm:mb-3 animate-fade-in flex-1 overflow-visible sm:overflow-hidden scrollbar-hide">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all text-xs flex items-center justify-center font-bold ${
                        selectedImageIndex === index
                          ? 'border-[#E5191F] dark:border-[#FCD016] ring-2 ring-[#E5191F]/30 dark:ring-[#FCD016]/30'
                          : 'border-gray-300 dark:border-[#5A5A5E] hover:border-[#E5191F] dark:hover:border-[#FCD016]'
                      }`}
                    >
                      <img
                        src={image.thumbnailUrl}
                        alt={`${index + 1}`}
                        className={`w-full h-full object-cover ${selectedImageIndex === index ? 'ring-2 ring-[#E5191F]' : ''}`}
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3C/svg%3E'
                        }}
                      />
                      <span className="absolute bottom-1 left-1 right-1 truncate rounded bg-black/45 px-1.5 py-0.5 text-[8px] sm:text-[9px] font-medium text-white text-left shadow-[0_1px_4px_rgba(0,0,0,0.7)] backdrop-blur-[1px] pointer-events-none">
                        {image.domain || (() => { try { return new URL(image.sourceUrl || image.imageUrl).hostname.replace(/^www\./, '') } catch { return 'origem desconhecida' } })()}
                      </span>
                      {selectedImageIndex === index && (
                        <div className="absolute top-0.5 right-0.5 bg-[#E5191F] dark:bg-[#FCD016] text-white dark:text-[#18181A] rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-xs font-bold">
                          <Check size={10} className="sm:block hidden" />
                          <Check size={8} className="sm:hidden" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

            {/* Preview */}
            {selectedImageIndex !== null && (
              <div className="bg-gray-50 dark:bg-[#18181A] rounded-lg overflow-hidden border border-[#E5191F] dark:border-[#FCD016] flex items-center justify-center max-h-48 sm:max-h-80 animate-scale-in">
                <img
                  src={images[selectedImageIndex].thumbnailUrl}
                  alt={`Preview ${selectedImageIndex + 1}`}
                  className="w-auto h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3C/svg%3E'
                  }}
                />
              </div>
            )}

                {/* Counter & Approve - Button Style */}
                {selectedImageIndex !== null && (
                  <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-2 sm:p-4 flex flex-col sm:flex-row items-center sm:items-center sm:justify-between gap-2 mt-2 animate-slide-up">
                    <div className="text-xs sm:text-sm text-white">
                      <span>
                        <span className="font-bold">{selectedImageIndex + 1}</span>/<span className="font-bold">{images.length}</span>
                      </span>
                    </div>

                    <button
                      onClick={handleApprove}
                      disabled={isLoading}
                      className="w-full sm:flex-1 px-2 sm:px-3 py-2 rounded-lg bg-white hover:bg-gray-100 text-green-600 font-bold text-xs sm:text-sm transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-1 sm:gap-2"
                    >
                      <Check size={14} className="sm:block hidden" />
                      <Check size={12} className="sm:hidden" />
                      Aprovar
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-4 border border-gray-200 dark:border-[#5A5A5E] flex-1 flex items-center justify-center">
                <p className="text-[#5A5A5E] dark:text-[#D9D9D9] text-center text-sm flex items-center justify-center gap-2">
                  <Search size={16} className="flex-shrink-0" />
                  <span>Pesquise imagens</span>
                </p>
              </div>
            )}

            {/* Search */}
            <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E] animate-slide-up">
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={isSearching}
                    className="flex-1 px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#18181A] border border-gray-200 dark:border-[#5A5A5E] focus:ring-2 focus:ring-[#E5191F] dark:focus:ring-[#FCD016] focus:border-transparent outline-none text-sm disabled:opacity-50 text-[#1F1F21] dark:text-white placeholder:text-[#5A5A5E] dark:placeholder:text-[#D9D9D9]"
                    placeholder="Pesquisar..."
                  />
                  <div className="flex gap-2">
<button
                      onClick={() => handleSearch()}
                      disabled={isSearching || !searchQuery}
                      className="px-2 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] text-white dark:text-[#18181A] font-semibold text-xs sm:text-sm transition-all hover:shadow-lg disabled:opacity-50 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                    >
                      <Search size={12} className="sm:block hidden flex-shrink-0" />
                      <Search size={10} className="sm:hidden flex-shrink-0" />
                      <span className="hidden sm:inline">{isSearching ? 'Buscando...' : 'Pesquisar'}</span>
                      <span className="sm:hidden">{isSearching ? 'B...' : 'Ok'}</span>
                    </button>
                  </div>
                </div>

                {images.length > 0 && (
                  <button
                    onClick={handleNextPage}
                    disabled={isSearching}
                    className="w-full px-3 py-2 rounded-lg bg-[#FCD016]/20 dark:bg-[#FCD016]/20 text-[#D9B20F] dark:text-[#FCD016] font-semibold text-xs sm:text-sm hover:bg-[#FCD016]/30 dark:hover:bg-[#FCD016]/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-1 sm:gap-2"
                  >
                    <ChevronRight size={12} className="sm:block hidden" />
                    <ChevronRight size={10} className="sm:hidden" />
                    <span>Próximas 5</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right: Preview & Upload & Options (1 col) */}
          <div className="order-3 sm:order-none col-span-1 sm:col-span-2 lg:col-span-1 space-y-2 sm:space-y-3 sm:overflow-y-auto sm:max-h-full">
            {/* Preview */}
            {(state?.status === 'APPROVED' || state?.status === 'ARCHIVED') && previewImage ? (
              <div className={`bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border-2 ${state?.status === 'ARCHIVED' ? 'border-slate-500' : 'border-green-500'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className={state?.status === 'ARCHIVED' ? 'text-slate-500 flex-shrink-0' : 'text-green-600 flex-shrink-0'} />
                  <h2 className={`text-xs font-semibold uppercase tracking-wide ${state?.status === 'ARCHIVED' ? 'text-slate-600 dark:text-slate-300' : 'text-green-700 dark:text-green-400'}`}>{state?.status === 'ARCHIVED' ? 'Arquivada' : 'Aprovada'}</h2>
                </div>

                <img
                  src={previewImage}
                  alt={`Preview ${product.codigo}`}
                  className="w-full h-32 sm:h-48 object-cover rounded-lg mb-2 border border-green-200 dark:border-green-800"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3C/svg%3E'
                  }}
                />

                <button
                  onClick={handleDownloadCurrent}
                  className="w-full text-center px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-xs sm:text-sm transition-all hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Download size={14} className="flex-shrink-0" />
                  Baixar
                </button>
              </div>
            ) : state?.status === 'DELETED' ? (
              <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-4 border-2 border-red-400 dark:border-red-700">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className="text-red-500 flex-shrink-0" />
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">Excluída</h2>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300">A imagem já foi concluída e posteriormente removida do Storage para liberar espaço. Este código continua marcado como produto com imagem.</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E] h-24 sm:h-24 flex items-center justify-center">
                <p className="text-[#5A5A5E] dark:text-[#D9D9D9] text-xs text-center">Nenhuma imagem salva</p>
              </div>
            )}

            {/* Upload Image */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border-2 transition-colors ${
                dragOverUpload
                  ? 'border-[#FCD016] dark:border-[#FFE066] bg-[#FCD016]/5 dark:bg-[#FCD016]/5'
                  : 'border-dashed border-gray-300 dark:border-[#5A5A5E]'
              }`}
            >
              <div className="text-center">
                <p className="text-xs font-semibold text-[#414142] dark:text-[#D9D9D9] mb-2 sm:mb-3 uppercase tracking-wide flex items-center justify-center gap-1">
                  <Upload size={12} className="flex-shrink-0" />
                  Upload de Imagem
                </p>
                <div className="space-y-1 sm:space-y-2">
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={isUploadingImage}
                      className="hidden"
                    />
                    <button
                      onClick={(e) => {
                        const input = (e.currentTarget.parentElement as HTMLElement).querySelector('input[type="file"]') as HTMLInputElement
                        input?.click()
                      }}
                      disabled={isUploadingImage}
                      className="w-full px-2 sm:px-3 py-2 rounded-lg bg-[#FCD016]/20 dark:bg-[#FCD016]/20 text-[#D9B20F] dark:text-[#FCD016] font-semibold text-xs sm:text-sm hover:bg-[#FCD016]/30 dark:hover:bg-[#FCD016]/30 transition-colors disabled:opacity-50 cursor-pointer flex items-center justify-center gap-1"
                    >
                      {isUploadingImage ? (
                        <>
                          <Loader size={12} className="animate-spin flex-shrink-0 sm:block hidden" />
                          <Loader size={10} className="animate-spin flex-shrink-0 sm:hidden" />
                          <span className="hidden sm:inline">Processando...</span>
                          <span className="sm:hidden">P...</span>
                        </>
                      ) : (
                        <>
                          <Folder size={12} className="flex-shrink-0 sm:block hidden" />
                          <Folder size={10} className="flex-shrink-0 sm:hidden" />
                          <span className="hidden sm:inline">Selecionar</span>
                          <span className="sm:hidden">Sel</span>
                        </>
                      )}
                    </button>
                  </label>
                  <p className="text-xs text-[#5A5A5E] dark:text-[#D9D9D9]">ou arrastar</p>
                  <p className="text-xs text-[#5A5A5E] dark:text-[#D9D9D9]">(JPG, PNG)</p>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="bg-white dark:bg-[#232326] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200 dark:border-[#5A5A5E] flex-1">
              <NoImageOptions
                onGoToBrands={() => onGoToBrands?.()}
                onGoToGallery={() => onGoToGallery?.()}
                onDeactivate={handleDeactivate}
                onMarkNoImage={handleNoImage}
                isLoading={isLoading}
              />

              {/* Completion */}
              {stats && stats.pending === 0 && (
                <button
                  onClick={() => {
                    if (window.confirm('Finalizar?')) {
                      handleCompleteSession()
                    }
                  }}
                  className="w-full mt-2 sm:mt-3 px-2 sm:px-3 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold text-xs sm:text-sm transition-all hover:shadow-lg flex items-center justify-center gap-1"
                >
                  <CheckCircle size={14} className="flex-shrink-0" />
                  Finalizar
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
